import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import { Member, Organ } from "../../types/user";
import { api } from "./index.api";
import { FilterQuery, PaginationQuery } from "../../types/query";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import toastError from "../../components/notif";
import { apiErrorMessage } from "../../utils/apiError";
import { Course } from "../../types/course";

const organEntityAdapter = createEntityAdapter<Organ, string>({
  selectId: (item: Organ) => item._id,
});

export type GetOrganType = {
  organs: EntityState<Organ, string>;
  length: number;
  from: number;
};

const membersEntityAdapter = createEntityAdapter<Member, string>({
  selectId: (item: Member) => item._id,
});

export type PendingMember = {
  _id: string;
  phone: string;
  createdAt?: string;
};

export type GetOrganMemberType = {
  members: EntityState<Member, string>;
  length: number;
  from: number;
  pending: PendingMember[];
};

// result of POST /organs/:organId/members
export type AddMembersResult = {
  added: string[];
  pending: string[];
  alreadyMember: string[];
  invalid?: string[];
};

// POST /admin/organs returns the generated password once
export type AddOrganResult = {
  _id: string;
  phone: string;
  name: string;
  password: string;
};

// one row per organ course grant: _id is the Access id (used to remove it)
type OrganCourseRow = Pick<Course, "_id" | "title" | "slug"> & {
  courseId?: string;
  organPrivate?: boolean;
};
const coursesEntityAdapter = createEntityAdapter<OrganCourseRow, string>({
  selectId: (item: OrganCourseRow) => item._id,
});

export type GetCourseType = {
  courses: EntityState<OrganCourseRow, string>;
  length: number;
  from: number;
};

function organsApiErrorHandler(error: FetchBaseQueryError) {
  switch (error.status) {
    case 409:
      return toastError("این شماره قبلاً ثبت شده است.");
    default:
      return toastError(apiErrorMessage(error));
  }
}

const organApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrgans: builder.query<
      GetOrganType,
      FilterQuery<Organ & PaginationQuery>
    >({
      query: (input) => ({
        url: "/admin/organs",
        params: input,
      }),
      transformResponse(
        baseQueryReturnValue: { organs: any; length: number; from: number },
        meta,
        arg
      ) {
        return {
          organs: organEntityAdapter.addMany(
            organEntityAdapter.getInitialState(),
            baseQueryReturnValue.organs as any
          ),
          length: baseQueryReturnValue.length,
          from: baseQueryReturnValue.from,
        };
      },
      providesTags: ["organs"],
    }),
    addOrgan: builder.mutation<AddOrganResult, FormData>({
      query: (input) => ({
        url: "/admin/organs",
        method: "POST",
        body: input,
      }),
      async onQueryStarted(arg, api) {
        try {
          await api.queryFulfilled;
        } catch (error: any) {
          organsApiErrorHandler(error.error as FetchBaseQueryError);
        }
      },
      invalidatesTags: (res, err, api) => {
        if (err) return [];
        return ["organs"];
      },
    }),
    updateOrgan: builder.mutation<void, { data: FormData; organId: string }>({
      query: (input) => ({
        url: `/admin/organs/${input.organId}`,
        method: "PUT",
        body: input.data,
      }),
      async onQueryStarted(arg, api) {
        try {
          await api.queryFulfilled;
        } catch (error: any) {
          organsApiErrorHandler(error.error as FetchBaseQueryError);
        }
      },
      invalidatesTags: (res, err, api) => {
        if (err) return [];
        return ["organs"];
      },
    }),
    deleteOrgan: builder.mutation<void, string>({
      query: (input) => ({
        url: `/admin/organs/${input}`,
        method: "DELETE",
      }),
      async onQueryStarted(arg, api) {
        try {
          await api.queryFulfilled;
        } catch (error: any) {
          organsApiErrorHandler(error.error as FetchBaseQueryError);
        }
      },
      invalidatesTags: (res, err, api) => {
        if (err) return [];
        return ["organs"];
      },
    }),
    getMembers: builder.query<
      GetOrganMemberType,
      {
        query: PaginationQuery;
        organId: string;
      }
    >({
      query: (input) => ({
        url: `/organs/${input.organId}/members`,
        params: input.query,
      }),
      transformResponse(
        baseQueryReturnValue: {
          members: any;
          length: number;
          from: number;
          pending?: PendingMember[];
        },
        meta,
        arg
      ) {
        return {
          members: membersEntityAdapter.addMany(
            membersEntityAdapter.getInitialState(),
            baseQueryReturnValue.members as any
          ),
          length: baseQueryReturnValue.length,
          from: baseQueryReturnValue.from,
          pending: baseQueryReturnValue.pending ?? [],
        };
      },
      providesTags: ["members"],
    }),
    addMembers: builder.mutation<
      AddMembersResult,
      { data: string[]; organId: string }
    >({
      query: (input) => ({
        url: `/organs/${input.organId}/members`,
        method: "POST",
        body: { members: input.data },
      }),
      async onQueryStarted(arg, api) {
        try {
          await api.queryFulfilled;
        } catch (error: any) {
          organsApiErrorHandler(error.error as FetchBaseQueryError);
        }
      },
      invalidatesTags: (res, err, api) => {
        if (err) return [];
        return ["members"];
      },
    }),
    removeMember: builder.mutation<void, { memberId: string; organId: string }>(
      {
        query: (input) => ({
          url: `/organs/${input.organId}/members/${input.memberId}`,
          method: "DELETE",
        }),
        async onQueryStarted(arg, api) {
          try {
            await api.queryFulfilled;
          } catch (error: any) {
            organsApiErrorHandler(error.error as FetchBaseQueryError);
          }
        },
        invalidatesTags: (res, err, api) => {
          if (err) return [];
          return ["members"];
        },
      }
    ),
    removePendingMember: builder.mutation<
      void,
      { pendingId: string; organId: string }
    >({
      query: (input) => ({
        url: `/organs/${input.organId}/members/pending/${input.pendingId}`,
        method: "DELETE",
      }),
      async onQueryStarted(arg, api) {
        try {
          await api.queryFulfilled;
        } catch (error: any) {
          organsApiErrorHandler(error.error as FetchBaseQueryError);
        }
      },
      invalidatesTags: (res, err, api) => {
        if (err) return [];
        return ["members"];
      },
    }),
    getOrganCourses: builder.query<
      GetCourseType,
      {
        query: PaginationQuery;
        organId: string;
      }
    >({
      query: (input) => ({
        url: `/organs/${input.organId}/courses`,
        params: input.query,
      }),
      transformResponse(
        baseQueryReturnValue: { courses: any; length: number; from: number },
        meta,
        arg
      ) {
        return {
          courses: coursesEntityAdapter.addMany(
            coursesEntityAdapter.getInitialState(),
            baseQueryReturnValue.courses as any
          ),
          length: baseQueryReturnValue.length,
          from: baseQueryReturnValue.from,
        };
      },
      providesTags: (res, err, api) => {
        if (err) return [];
        return [{ type: "organs-courses", id: api.organId }];
      },
    }),
    addCourses: builder.mutation<void, { data: string[]; organId: string }>({
      query: (input) => ({
        url: `/organs/${input.organId}/courses/add`,
        method: "POST",
        body: { courses: input.data },
      }),
      async onQueryStarted(arg, api) {
        try {
          await api.queryFulfilled;
        } catch (error: any) {
          organsApiErrorHandler(error.error as FetchBaseQueryError);
        }
      },
      invalidatesTags: (res, err, api) => {
        if (err) return [];
        return [{ type: "organs-courses", id: api.organId }];
      },
    }),
    removeCourse: builder.mutation<void, { accessId: string; organId: string }>(
      {
        query: (input) => ({
          url: `/organs/${input.organId}/courses/remove/${input.accessId}`,
          method: "DELETE",
        }),
        async onQueryStarted(arg, api) {
          try {
            await api.queryFulfilled;
          } catch (error: any) {
            organsApiErrorHandler(error.error as FetchBaseQueryError);
          }
        },
        invalidatesTags: (res, err, api) => {
          if (err) return [];
          return [{ type: "organs-courses", id: api.organId }];
        },
      }
    ),
  }),
  overrideExisting: true,
});

export const {
  useGetOrgansQuery,
  useAddOrganMutation,
  useUpdateOrganMutation,
  useDeleteOrganMutation,
  useGetMembersQuery,
  useAddMembersMutation,
  useRemoveMemberMutation,
  useRemovePendingMemberMutation,
  useGetOrganCoursesQuery: useGetCoursesQuery,
  useAddCoursesMutation,
  useRemoveCourseMutation,
} = organApi;
